"use client";

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import WaitingRoomColumn from "../../components/WaitingRoomColumn/WaitingRoomColumn";
import WaitingPatientCard from "../../components/WaitingPatientCard/WaitingPatientCard";
import BookAppointmentModal from "../../components/BookAppointmentModal/BookAppointmentModal";
import BookDashboard from "../../../cabinetDashboard/components/BookDashboard/BookDashboard";
import PaymentRequiredModal from "../../components/PaymentRequiredModal/PaymentRequiredModal";
import "./WaitingRoom.css";

import useDoctorConfirmedTodayForWaitingRoom, {
  recomputeInConsultation,
} from "../../hooks/useDoctorConfirmedTodayForWaitingRoom";
import {
  createRoomOnArrival,
  updateRoomStatus,
} from "../../redux/thunks/room.thunks";

import { fetchUsers } from "../../../Profile/redux/thunks/userThunks";
import useConsultations from "../../../consultation/hooks/useConsultations";
import { fetchPatientRendezvousByExternalId } from "../../../appointments/redux/thunks/rendezvousThunks";
import { fetchRendezvousByMedecin } from "../../../appointments/redux/thunks/rendezvousThunks";

/* ─── Search Icon ─── */
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ─── Column empty-state icons ─── */
const ConfirmedEmptyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 2v3" />
    <path d="M16 2v3" />
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M8 13h.01" />
    <path d="M12 13h.01" />
    <path d="M16 13h.01" />
    <path d="M8 17h.01" />
    <path d="M12 17h.01" />
    <path d="M16 17h.01" />
  </svg>
);

const WaitingEmptyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CompletedEmptyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const selectAllPatients = (state) => {
  const u = state.user;
  if (!u) return [];
  if (u.usersById && typeof u.usersById === "object") return Object.values(u.usersById);
  if (Array.isArray(u.list)) return u.list;
  return [];
};

      const getAppointmentDate = (value) => {
        if (value?.startTime || value?.dateTime) {
          return new Date(value.startTime || value.dateTime);
        }
        const rawDate = value?.date || value?.appointmentDate;
        const rawTime = value?.time || value?.appointmentTime || "00:00";
        if (!rawDate) return new Date(NaN);
        const parts = String(rawDate).split(/[/-]/);
        const normalizedDate =
          parts.length === 3 && parts[0].length === 2
            ? `${parts[2]}-${parts[1]}-${parts[0]}`
            : String(rawDate);
        return new Date(`${normalizedDate}T${rawTime}`);
      };
function findContainer(patients, id) {
  return patients.find((p) => p.id === id)?.status || null;
}

const getPaymentCacheKeys = (patient) =>
  [
    patient?.roomId,
    patient?.id,
    patient?.appointmentId ? `appointment-${patient.appointmentId}` : null,
    patient?.consultationId ? `consultation-${patient.consultationId}` : null,
  ].filter(Boolean);

const getCachedPayment = (cacheRef, patient) => {
  const cached =
    getPaymentCacheKeys(patient)
      .map((key) => cacheRef.current[key])
      .find(Boolean) || {};

  return {
    consultationId: cached.consultationId || patient?.consultationId || null,
    paymentStatus: cached.paymentStatus || patient?.paymentStatus || "unpaid",
    amount: cached.amount || patient?.amount || "",
    paymentType: cached.paymentType || patient?.paymentType || null,
    paymentMethod: cached.paymentMethod || patient?.paymentMethod || null,
  };
};

/* ─── Consultation amounts (price saved in the consultation) ─── */

/* "110.000 DT" -> 110 ; "70" -> 70 ; 70 -> 70 */
const toAmountNumber = (value) => {
  const number = Number(
    String(value ?? "")
      .replace(",", ".")
      .replace(/[^\d.]/g, "")
  );

  return Number.isFinite(number) ? number : 0;
};

const normalizeConsultationList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.consultations)) return value.consultations;
  return [];
};

const unwrapConsultation = (value) => {
  if (!value) return null;

  if (
    value.data &&
    typeof value.data === "object" &&
    !Array.isArray(value.data)
  ) {
    return value.data;
  }

  return value;
};

const getConsultationDate = (consultation) => {
  const date = new Date(
    consultation?.consultationDateTime ||
      consultation?.createdAt ||
      consultation?.updatedAt ||
      0
  );

  return Number.isNaN(date.getTime()) ? null : date;
};

const getConsultationTimestamp = (consultation) =>
  getConsultationDate(consultation)?.getTime() || 0;

const isSameLocalDay = (first, second) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

/*
 * Honoraires + total des actes médicaux enregistrés dans la consultation.
 * Actes = somme(KC x cotation) si disponible, sinon amount - honoraires.
 */
const getConsultationAmounts = (consultation) => {
  const consultationPrice = toAmountNumber(consultation?.consultationPrice);

  const acts = Array.isArray(consultation?.medicalActs)
    ? consultation.medicalActs
    : [];

  let medicalActsTotal = Number(consultation?.medicalActsTotal) || acts.reduce((total, act) => {
    const key = Number(act?.key);
    const coefficient = Number.isFinite(key) && key > 0 ? key : 1;

    return total + coefficient * toAmountNumber(act?.cotation);
  }, 0);

  if (!medicalActsTotal) {
    const total = toAmountNumber(consultation?.amount);

    if (total > consultationPrice) {
      medicalActsTotal = total - consultationPrice;
    }
  }

  return {
    consultationPrice,
    medicalActsTotal,
  };
};

/* ─── Search filter ─── */
function matchesSearch(patient, query) {
  if (!query.trim()) return true;

  const q = query.trim().toLowerCase();

  // Normalize date: "1998-05-12" → also match "12/05/1998", "05/12/1998", "1998/05/12"
  const normalizeDate = (val) => {
    if (!val) return [];
    const s = String(val);
    // already matches as-is; also add slash variants
    const parts = s.split(/[-/]/);
    if (parts.length === 3) {
      return [s, parts.join("/"), `${parts[2]}/${parts[1]}/${parts[0]}`, `${parts[1]}/${parts[2]}/${parts[0]}`];
    }
    return [s];
  };

  const fields = [
    patient.name,
    patient.firstName,
    patient.lastName,
    patient.identifier,
    patient.ptn,
    patient.email,
    patient.phone,
    patient.phoneNumber,
    ...normalizeDate(patient.birthdate),
    ...normalizeDate(patient.dateOfBirth),
    ...normalizeDate(patient.dob),
    ...normalizeDate(patient.birthDate),
  ];

  return fields.some((f) => f && String(f).toLowerCase().includes(q));
}

/* ─── Component ─── */
export default function WaitingRoom({
  LayoutComponent = AppLayoutDoctor,
  disableRemote = false,
  localPatients = [],
  showOpenFile = true,
  doctorIdOverride = null,
  onOpenPaymentHistory,
}) {
  const { t } = useTranslation("waitingRoom");
  const dispatch = useDispatch();

  const {
    patients: confirmedTodayPatients,
    loading,
    error,
  } = useDoctorConfirmedTodayForWaitingRoom({
    disableRemote,
    localPatientsData: localPatients,
    doctorIdOverride,
  });

  const {
    create: createConsultation,
    getById: fetchConsultationById,
    getByPatientId: fetchConsultationsByPatient,
  } = useConsultations();

  const allPatients = useSelector(selectAllPatients);
  const currentDoctor = useSelector((state) => state.doctor?.user);

  const [patients, setPatients] = useState([]);
  const [activePatient, setActivePatient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reschedulePatient, setReschedulePatient] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const draggedSnapshotRef = useRef(null);
  const paymentCacheRef = useRef({});
  const searchInputRef = useRef(null);
  const usersFetchStartedRef = useRef(false);

  useEffect(() => {
    if (usersFetchStartedRef.current) return;
    if (allPatients && allPatients.length > 0) return;
    usersFetchStartedRef.current = true;
    dispatch(fetchUsers());
  }, [allPatients, dispatch]);

  useEffect(() => {
    if (loading || !confirmedTodayPatients?.length) return;

    // Merge instead of replace: the hook's output can re-render (new array
    // reference) for reasons unrelated to this patient's workflow status
    // (e.g. fetchUserById updating usersById). Overwriting wholesale would
    // revert an already-dropped patient back to "confirmed", which made it
    // look like only one drag-and-drop could ever succeed in a session.
    setPatients((prev) => {
      const prevById = new Map(prev.map((p) => [String(p.id), p]));
      const merged = confirmedTodayPatients.map((incoming) => {
        const existing = prevById.get(String(incoming.id));
        const withPayment = { ...incoming, ...getCachedPayment(paymentCacheRef, incoming) };
        if (!existing) return withPayment;
        return { ...withPayment, status: existing.status, inConsultation: existing.inConsultation };
      });
      return recomputeInConsultation(merged);
    });
  }, [confirmedTodayPatients, loading]);

  const medecinId =
    doctorIdOverride ||
    currentDoctor?.doctorId ||
    currentDoctor?.pgId ||
    currentDoctor?.id ||
    confirmedTodayPatients?.[0]?.medecinId ||
    confirmedTodayPatients?.[0]?.doctorId ||
    null;

  // Map patientId -> full user profile (email, birthdate...) from Redux store
  const allPatientsMap = useMemo(() => {
    const map = {};
    allPatients.forEach((u) => {
      if (u?.id) map[String(u.id)] = u;
    });
    return map;
  }, [allPatients]);

  // Enrich each waiting-room patient with profile fields missing from room data
  const enrichedPatients = useMemo(() => {
    return patients.map((p) => {
      const pid = String(p.patientId || p.userId || "");
      const profile = allPatientsMap[pid] || {};

      const resolvedEmail = p.email || profile.email || null;
      const resolvedBirthdate =
        p.birthdate || p.dateOfBirth || p.dob || p.birthDate ||
        profile.birthdate || profile.dateOfBirth || profile.dob || profile.birthDate || null;

      return {
        ...p,
        email: resolvedEmail,
        birthdate: resolvedBirthdate,
        dateOfBirth: resolvedBirthdate,
        phone: p.phone || p.phoneNumber || profile.phone || profile.phoneNumber || null,
        firstName: p.firstName || profile.firstName || null,
        lastName: p.lastName || profile.lastName || null,
      };
    });
  }, [patients, allPatientsMap]);

  /* --- Filtered patients --- */
  const filteredPatients = useMemo(
    () => enrichedPatients.filter((p) => matchesSearch(p, searchQuery)),
    [enrichedPatients, searchQuery]
  );

  const searchResultCount = filteredPatients.length;
  const isSearchActive = searchQuery.trim().length > 0;

  const COLUMNS = useMemo(
    () => [
      {
        id: "confirmed",
        title: t("columns.confirmed"),
        accentColor: "#3b82f6",
        emptyIcon: <ConfirmedEmptyIcon />,
        emptyText: t("empty.confirmed"),
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M8 3v4M16 3v4M3 10h18M9 15l2 2 4-4" />
          </svg>
        ),
      },
      {
        id: "waiting",
        title: t("columns.waiting"),
        accentColor: "#8b5cf6",
        emptyIcon: <WaitingEmptyIcon />,
        emptyText: t("empty.waiting"),
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ),
      },
      {
        id: "completed",
        title: t("columns.completed"),
        accentColor: "#10b981",
        emptyIcon: <CompletedEmptyIcon />,
        emptyText: t("empty.completed"),
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
        ),
      },
    ],
    [t]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const syncRoomStatus = useCallback(
    async (roomId, status) => {
      if (!roomId || !status) return;
      let actualRoomId = roomId;
      const temporaryRoomMatch = String(roomId).match(/^appointment-(\d+)$/);

      if (temporaryRoomMatch) {
        const room = await dispatch(
          createRoomOnArrival({ appointmentId: Number(temporaryRoomMatch[1]) }),
        ).unwrap();
        actualRoomId = room?.roomId || room?.id;

        if (status === "waiting") return;
      }

      if (actualRoomId) {
        if (status === "completed") {
          await dispatch(
            updateRoomStatus({ roomId: actualRoomId, status: "in_consultation" }),
          ).unwrap();
        }
        await dispatch(updateRoomStatus({ roomId: actualRoomId, status }));
      }
    },
    [dispatch]
  );

  const syncWaitingToCompleted = useCallback(
    async (roomId) => {
      if (!roomId) return;
      let actualRoomId = roomId;
      const temporaryRoomMatch = String(roomId).match(/^appointment-(\d+)$/);

      if (temporaryRoomMatch) {
        const room = await dispatch(
          createRoomOnArrival({ appointmentId: Number(temporaryRoomMatch[1]) }),
        ).unwrap();
        actualRoomId = room?.roomId || room?.id;
      }

      if (!actualRoomId) return;
      try {
        await dispatch(updateRoomStatus({ roomId: actualRoomId, status: "in_consultation" })).unwrap();
      } catch (err) {
        const message = String(err?.message || "");
        if (!message.includes("in_consultation")) {
          console.error("Erreur transition vers in_consultation:", err);
          return;
        }
      }
      try {
        await dispatch(updateRoomStatus({ roomId: actualRoomId, status: "completed" })).unwrap();
      } catch (err) {
        console.error("Erreur transition vers completed:", err);
      }
    },
    [dispatch]
  );

  const syncAfterReorder = useCallback(
    (updatedPatients, previousFirstId) => {
      const waitingCol = updatedPatients.filter((p) => p.status === "waiting");
      const newFirst = waitingCol[0];
      if (!newFirst || newFirst.id === previousFirstId) return;
      syncRoomStatus(newFirst.roomId, "in_consultation");
      if (previousFirstId) {
        const oldFirst = updatedPatients.find(
          (p) => p.id === previousFirstId && p.status === "waiting"
        );
        if (oldFirst) syncRoomStatus(oldFirst.roomId, "waiting");
      }
    },
    [syncRoomStatus]
  );

  const openPaymentModal = useCallback((patient) => {
    if (!patient) return;
    setPaymentModal({
      patient,
      consultation: null,
      waitingRoomPayment: null,
      consultationPrice: Number(patient.consultationPrice) || 70,
      medicalActsTotal: Number(patient.medicalActsTotal) || 0,
      alreadyPaid: 0,
      paymentHistory: [],
    });
  }, []);

  const handleOpenPayment = useCallback((patient) => {
    openPaymentModal(patient);
  }, [openPaymentModal]);

  const handleReschedule = useCallback((patient) => {
    setPaymentModal(null);
    setReschedulePatient(patient);
  }, []);

  const handleRemove = useCallback((id) => {
    setPatients((prev) => recomputeInConsultation(prev.filter((p) => p.id !== id)));
  }, []);

  const handleRequestPayment = useCallback((patient) => {
    openPaymentModal(patient);
  }, [openPaymentModal]);

  const handleLoadPatientAppointments = useCallback(
    async (patient) => {
      const patientIds = [
        patient?.patientId,
        patient?.userId,
        patient?.principalUserId,
        patient?.effectiveProfileId,
      ].filter(Boolean).map(String).filter((id, index, ids) => ids.indexOf(id) === index);
      if (!patientIds.length) return [];

      const responses = await Promise.all(
        patientIds.map((userId) =>
          dispatch(fetchPatientRendezvousByExternalId({ userId }))
            .unwrap()
            .catch(() => [])
        )
      );
      let appointments = responses.flatMap((result) =>
        Array.isArray(result)
          ? result
          : Array.isArray(result?.items)
          ? result.items
          : Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.appointments)
          ? result.appointments
          : []
      );

      if (!appointments.length && medecinId) {
        const doctorResult = await dispatch(
          fetchRendezvousByMedecin({ userId: medecinId, query: {} })
        ).unwrap().catch(() => []);
        const doctorAppointments = Array.isArray(doctorResult)
          ? doctorResult
          : Array.isArray(doctorResult?.items)
          ? doctorResult.items
          : Array.isArray(doctorResult?.data)
          ? doctorResult.data
          : [];
        appointments = doctorAppointments.filter((appointment) =>
          patientIds.includes(
            String(
              appointment?.patientId ||
                appointment?.userId ||
                appointment?.effectiveProfileId ||
                appointment?.subprofileId ||
                ""
            )
          )
        );
      }
      const getAppointmentDate = (appointment) => {
        if (appointment?.startTime || appointment?.dateTime) {
          return new Date(appointment.startTime || appointment.dateTime);
        }
        const rawDate = appointment?.date || appointment?.appointmentDate;
        const rawTime = appointment?.time || appointment?.appointmentTime || "00:00";
        if (rawDate) {
          const dateParts = String(rawDate).split(/[/-]/);
          const normalizedDate =
            dateParts.length === 3 && dateParts[0].length === 2
              ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
              : String(rawDate);
          return new Date(`${normalizedDate}T${rawTime}`);
        }
        return new Date(NaN);
      };
      const now = Date.now();

      return appointments
        .filter((appointment) =>
          !["cancelled", "canceled", "rejected", "completed"].includes(
            String(appointment?.status || "").toLowerCase()
          )
        )
        .filter((appointment) => getAppointmentDate(appointment).getTime() >= now)
        .sort(
          (first, second) =>
            getAppointmentDate(first).getTime() -
            getAppointmentDate(second).getTime()
        );
    },
    [dispatch, medecinId]
  );

  const handleLoadPatientDetails = useCallback(
    async (patient) => {
      const patientId = patient?.patientId || patient?.userId;
      if (!patientId) return {};

      const consultationRequest = patient?.consultationId
        ? fetchConsultationById(patient.consultationId)
            .unwrap()
          .catch(() => fetchConsultationsByPatient(patientId).unwrap())
          .catch(() => null)
        : fetchConsultationsByPatient(patientId).unwrap().catch(() => null);
      const [appointments, consultations] = await Promise.all([
        handleLoadPatientAppointments(patient).catch(() => []),
        consultationRequest,
      ]);
      const consultationList = Array.isArray(consultations)
        ? consultations
        : Array.isArray(consultations?.data)
        ? consultations.data
        : Array.isArray(consultations?.items)
        ? consultations.items
        : Array.isArray(consultations?.consultations)
        ? consultations.consultations
        : consultations
        ? [consultations]
        : [];
      const latestConsultation = [...consultationList].sort(
        (first, second) => {
          const firstMatches =
            String(first?.id || "") === String(patient?.consultationId || "");
          const secondMatches =
            String(second?.id || "") === String(patient?.consultationId || "");
          if (firstMatches !== secondMatches) return secondMatches - firstMatches;
          return (
            new Date(second.updatedAt || second.createdAt || 0) -
            new Date(first.updatedAt || first.createdAt || 0)
          );
        }
      )[0];
      const appointment = appointments[0] || null;
      const appointmentDate = appointment
        ? getAppointmentDate(appointment)
        : null;

      return {
        nextAppointment: appointment
          ? { ...appointment, displayDate: appointmentDate.toLocaleString() }
          : null,
        payment: latestConsultation
          ? {
              paymentStatus:
                latestConsultation.paymentStatus ||
                latestConsultation.payment?.status ||
                "unpaid",
              amount:
                latestConsultation.amount ||
                latestConsultation.totalAmount ||
                latestConsultation.total ||
                latestConsultation.payment?.amount ||
                "",
            }
          : null,
      };
    },
    [fetchConsultationsByPatient, handleLoadPatientAppointments]
  );

  const handleBooked = useCallback(
    async (booked, patient, selectedDate, selectedTime, reason) => {
      if (!medecinId || !booked) return;
      const normalizedSource =
        String(booked.source || patient?.source || "Medilink").toLowerCase() === "local"
          ? "local"
          : "Medilink";

      setPatients((prev) =>
        recomputeInConsultation([
          ...prev,
          {
            id: booked.id || booked.roomId || booked.appointmentId,
            consultationId: booked.consultationId || null,
            roomId: booked.roomId || booked.id || null,
            appointmentId: booked.appointmentId || null,
            patientId: booked.patientId || patient?.id || null,
            subprofileId: booked.subprofileId || patient?.subprofileId || null,
            effectiveProfileId:
              booked.effectiveProfileId ||
              patient?.effectiveProfileId ||
              booked.subprofileId ||
              patient?.subprofileId ||
              booked.patientId ||
              patient?.id ||
              null,
            doctorId: booked.doctorId || medecinId,
            name: booked.name || patient?.name || "Patient",
            firstName: booked.firstName || patient?.firstName || "",
            lastName: booked.lastName || patient?.lastName || "",
            age: booked.age || patient?.age || 30,
            status: booked.status || "confirmed",
            source: normalizedSource,
            time: booked.time || selectedTime || "",
            reason: booked.reason || reason || "Consultation",
            paymentStatus: booked.paymentStatus || "unpaid",
            amount: booked.amount || "",
            paymentType: booked.paymentType || null,
            paymentMethod: booked.paymentMethod || null,
            waitingTime: booked.waitingTime ?? null,
            startTime: booked.startTime || null,
          },
        ])
      );
    },
    [medecinId]
  );

  const handlePaymentConfirm = useCallback(
    async ({ amount, paymentType, paymentMethod, consultationPrice, medicalActsTotal, paymentEntry, totalAmount }) => {
      const patient = paymentModal?.patient;
      if (!patient) return;

      try {
        const consultFee = Number(consultationPrice ?? paymentModal.waitingRoomPayment?.consultationPrice ?? paymentModal.consultation?.consultationPrice ?? patient.consultationPrice ?? 70) || 0;
        const actsFee = Number(medicalActsTotal ?? paymentModal.waitingRoomPayment?.medicalActsTotal ?? patient.medicalActsTotal ?? 0) || 0;
        const total = Number(totalAmount) || consultFee + actsFee;
        const patientId = String(patient.patientId || patient.userId || "").trim();
        const doctorId = String(
          patient.doctorId || patient.medecinId || medecinId || doctorIdOverride || ""
        ).trim();
        const appointmentId = Number(patient.appointmentId);
        if (!patientId || !doctorId || !Number.isInteger(appointmentId)) {
          throw new Error("Patient, doctor, and appointment are required to create a consultation payment.");
        }

        const paymentHistory = [
          paymentEntry || { amount: toAmountNumber(amount), method: paymentMethod || "cash", paidAt: new Date().toISOString() },
        ];
        const paidTotal = paymentHistory.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
        const isPaid = paidTotal >= total - 0.001;
        const paymentData = {
          paymentStatus: isPaid ? "paid" : "partial",
          amount: `${paidTotal.toFixed(3)} DT`,
          paymentType: isPaid ? "full" : paymentType || "installment",
          paymentMethod: paymentMethod || "cash",
          paymentHistory,
          consultationPrice: consultFee,
          medicalActsTotal: actsFee,
        };

        const createdConsultation = await createConsultation({
          userId: patientId,
          subprofileId: patient.subprofileId || undefined,
          doctorId,
          appointmentId,
          consultationDateTime: new Date().toISOString(),
          title: "Consultation",
          sections: [],
          consultationPrice: consultFee,
          paymentStatus: paymentData.paymentStatus,
          amount: paidTotal.toFixed(3),
          paymentType: paymentData.paymentType,
          paymentMethod: paymentData.paymentMethod,
          paymentHistory,
          medicalActsTotal: actsFee,
        }).unwrap();
        const consultationId = createdConsultation?.id || createdConsultation?.consultationId;
        if (!consultationId) {
          throw new Error("Consultation was created but its ID was not returned; verify it before retrying payment.");
        }
        paymentData.consultationId = consultationId;

        if (paymentModal?.mode === "completion") {
          await syncWaitingToCompleted(patient.roomId);

          setPatients((prev) => {
            const updated = prev.map((p) =>
              p.id === patient.id || p.roomId === patient.roomId
                ? {
                    ...p,
                    status: "completed",
                    inConsultation: false,
                    ...paymentData,
                    consultationPrice: consultFee,
                    medicalActsTotal: actsFee,
                  }
                : p
            );
            const recomputed = recomputeInConsultation(updated);
            const newFirst = recomputed.find((p) => p.status === "waiting" && p.inConsultation);
            if (newFirst) syncRoomStatus(newFirst.roomId, "in_consultation");
            return recomputed;
          });

          setPaymentModal(null);
          return;
        }

        getPaymentCacheKeys({ ...patient, consultationId }).forEach((key) => {
          paymentCacheRef.current[key] = paymentData;
        });

        setPatients((prev) =>
          recomputeInConsultation(
            prev.map((p) =>
              p.id === patient.id || p.roomId === patient.roomId
                ? { ...p, ...paymentData }
                : p
            )
          )
        );

        setPaymentModal(null);
      } catch (error) {
        console.error("Erreur paiement consultation:", error);
        throw error;
      }
    },
    [
      paymentModal,
      createConsultation,
      medecinId,
      doctorIdOverride,
      syncRoomStatus,
      syncWaitingToCompleted,
    ]
  );

  const handlePaymentClose = useCallback(() => {
    setPaymentModal(null);
  }, []);

  /*
   * Retrouve la consultation enregistrée pour ce patient :
   *  1. par consultationId si la carte le connaît ;
   *  2. sinon parmi les consultations du patient : celle liée au même
   *     rendez-vous, ou à défaut la plus récente d'AUJOURD'HUI.
   * Retourne null si rien n'est trouvé (le popup garde alors ses valeurs
   * par défaut).
   */
  const findConsultationForPatient = useCallback(
    async (patient) => {
      try {
        if (patient?.consultationId) {
          const response = await fetchConsultationById(
            patient.consultationId
          ).unwrap();

          return unwrapConsultation(response);
        }

        const userId = String(
          patient?.patientId || patient?.userId || ""
        ).trim();

        const doctorId = String(
          patient?.doctorId ||
            patient?.medecinId ||
            medecinId ||
            doctorIdOverride ||
            ""
        ).trim();

        if (!userId) {
          return null;
        }

        const response = await fetchConsultationsByPatient(
          userId,
          doctorId || undefined
        ).unwrap();

        const list = normalizeConsultationList(response);

        if (!list.length) {
          return null;
        }

        const newestFirst = (items) =>
          [...items].sort(
            (first, second) =>
              getConsultationTimestamp(second) -
              getConsultationTimestamp(first)
          );

        if (patient?.appointmentId) {
          const sameAppointment = list.filter(
            (consultation) =>
              String(consultation?.appointmentId ?? "") ===
              String(patient.appointmentId)
          );

          if (sameAppointment.length) {
            return newestFirst(sameAppointment)[0];
          }
        }

        const today = new Date();

        const fromToday = list.filter((consultation) => {
          const date = getConsultationDate(consultation);

          return date ? isSameLocalDay(date, today) : false;
        });

        return fromToday.length ? newestFirst(fromToday)[0] : null;
      } catch (error) {
        console.error(
          "Impossible de charger la consultation du patient:",
          error
        );

        return null;
      }
    },
    [
      fetchConsultationById,
      fetchConsultationsByPatient,
      medecinId,
      doctorIdOverride,
    ]
  );

  /*
   * Ouvre le popup "Consultation Summary" pré-rempli avec le prix
   * enregistré dans la consultation (honoraires + actes médicaux).
   */
  const openCompletionModal = useCallback(
    async (snap, cachedPayment) => {
      const consultation = await findConsultationForPatient(snap);
      const waitingRoomPayment =
        snap.waitingRoomPayment ||
        cachedPayment?.waitingRoomPayment ||
        null;

      const amounts = consultation
        ? getConsultationAmounts(consultation)
        : waitingRoomPayment
        ? {
            consultationPrice: Number(waitingRoomPayment.consultationPrice || 0),
            medicalActsTotal: Number(waitingRoomPayment.medicalActsTotal || 0),
          }
        : null;
      const paymentHistory = [];

      setPaymentModal({
        patient: {
          ...snap,
          ...cachedPayment,
          waitingRoomPayment,
          ...(amounts
            ? {
                ...amounts,
                consultationId:
                  consultation?.id ||
                  cachedPayment?.consultationId ||
                  snap?.consultationId ||
                  null,
              }
            : {}),
          status: "completed",
          inConsultation: false,
        },
        consultation,
        waitingRoomPayment,
        consultationPrice: amounts?.consultationPrice,
        medicalActsTotal: amounts?.medicalActsTotal,
        alreadyPaid: 0,
        paymentHistory,
        mode: "completion",
      });
    },
    [findConsultationForPatient]
  );

  const handleAdvance = useCallback(
    (id) => {
      const patient = patients.find((p) => p.id === id);
      if (!patient) return;

      const cachedPayment = getCachedPayment(paymentCacheRef, patient);

      if (patient.status === "confirmed") {
        setPatients((prev) => {
          syncRoomStatus(patient.roomId, "waiting");
          return recomputeInConsultation(
            prev.map((p) =>
              p.id === id ? { ...p, ...cachedPayment, status: "waiting" } : p
            )
          );
        });
        return;
      }

      if (patient.status === "waiting") {
        openCompletionModal(patient, cachedPayment);
      }
    },
    [patients, openCompletionModal, syncRoomStatus]
  );

  const handleDragStart = useCallback(
    ({ active }) => {
      const found = patients.find((p) => p.id === active.id) || null;
      setActivePatient(found);
      draggedSnapshotRef.current = found ? { ...found } : null;
    },
    [patients]
  );

  const handleDragOver = useCallback(() => {}, []);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      setActivePatient(null);
      const snap = draggedSnapshotRef.current;
      draggedSnapshotRef.current = null;
      if (!over || !snap) return;

      const fromColumn = snap.status;
      const toColumn = COLUMNS.find((c) => c.id === over.id)
        ? over.id
        : findContainer(patients, over.id);

      if (!fromColumn || !toColumn) return;

      const cachedPayment = getCachedPayment(paymentCacheRef, snap);

      if (fromColumn === "confirmed" && toColumn === "waiting") {
        setPatients((prev) => {
          syncRoomStatus(snap.roomId, "waiting");
          const updated = prev.map((p) =>
            p.id === active.id ? { ...p, ...cachedPayment, status: "waiting" } : p
          );
          return recomputeInConsultation(updated);
        });
        return;
      }

      if (fromColumn === "waiting" && toColumn === "completed") {
        openCompletionModal(snap, cachedPayment);
        return;
      }

      if (fromColumn === "confirmed" && toColumn === "completed") {
        setPatients((prev) => {
          syncRoomStatus(snap.roomId, "completed");
          const updated = prev.map((p) =>
            p.id === active.id
              ? { ...p, ...cachedPayment, status: "completed", inConsultation: false }
              : p
          );
          return recomputeInConsultation(updated);
        });
        return;
      }

      if (fromColumn === "waiting" && toColumn === "waiting") {
        setPatients((prev) => {
          const previousFirstId = prev.find((p) => p.status === "waiting" && p.inConsultation)?.id;
          const colItems = prev.filter((p) => p.status === "waiting");
          const rest = prev.filter((p) => p.status !== "waiting");
          const oldIdx = colItems.findIndex((p) => p.id === active.id);
          const newIdx = colItems.findIndex((p) => p.id === over.id);
          if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return prev;
          const reordered = arrayMove(colItems, oldIdx, newIdx);
          const recomputed = recomputeInConsultation([...rest, ...reordered]);
          syncAfterReorder(recomputed, previousFirstId);
          return recomputed;
        });
        return;
      }

      if (fromColumn !== toColumn) {
        setPatients((prev) => {
          const updated = prev.map((p) =>
            p.id === active.id ? { ...p, ...cachedPayment, status: toColumn } : p
          );
          const recomputed = recomputeInConsultation(updated);
          syncRoomStatus(snap.roomId, toColumn);
          return recomputed;
        });
      }
    },
    [
      patients,
      COLUMNS,
      syncRoomStatus,
      syncAfterReorder,
      openCompletionModal,
    ]
  );

  const byStatus = (status) => filteredPatients.filter((p) => p.status === status);
  const total = patients.length;

  return (
    <LayoutComponent>
      <div className="wr">
        {/* ── Header ── */}
        <div className="wr__header">
          <div className="wr__header-left">
            <h1 className="wr__title">{t("page.title")}</h1>
            <p className="wr__subtitle">
              {t("page.subtitle")} &bull; <strong>{total}</strong>{" "}
              {t("page.total")}
            </p>
          </div>

          <div className="wr__header-right">
            <span className="wr__live-badge">
              <span className="wr__live-dot" />
              {t("page.live")} &bull; {t("page.dragToUpdate")}
            </span>

            <button className="wr__book-btn" onClick={() => setShowModal(true)}>
              + {t("page.bookAppointment")}
            </button>
          </div>
        </div>

        {/* ── Search bar ── */}
        <div className="wr__search-wrapper">
          <div className="wr__search-bar">
            <span className="wr__search-icon" aria-hidden="true">
              <SearchIcon />
            </span>

            <input
              ref={searchInputRef}
              type="text"
              className="wr__search-input"
              placeholder={t("search.placeholder", "Search by name, PTN, email or birthdate…")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t("search.label", "Search patients")}
            />

            {isSearchActive && (
              <button
                type="button"
                className="wr__search-clear"
                onClick={() => {
                  setSearchQuery("");
                  searchInputRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                <ClearIcon />
              </button>
            )}
          </div>

          {isSearchActive && (
            <span className="wr__search-results">
              {searchResultCount === 0
                ? t("search.noResults", "No patients found")
                : t("search.results", "{{count}} patient(s) found", { count: searchResultCount })}
            </span>
          )}
        </div>

        {loading && (
          <div style={{ padding: "0.5rem 1rem" }}>
            {t("columns.loading", "Loading rooms...")}
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: "0.5rem 1rem", color: "red" }}>{error}</div>
        )}

        {/* ── Kanban columns ── */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="wr__columns">
            {COLUMNS.map((col) => {
              const colPatients = byStatus(col.id);
              return (
                <WaitingRoomColumn
                  key={col.id}
                  id={col.id}
                  title={col.title}
                  icon={col.icon}
                  accentColor={col.accentColor}
                  emptyIcon={col.emptyIcon}
                  emptyText={col.emptyText}
                  patients={colPatients}
                  count={colPatients.length}
                  onRemovePatient={handleRemove}
                  onAdvancePatient={handleAdvance}
                  onOpenPayment={handleOpenPayment}
                  onOpenPaymentHistory={onOpenPaymentHistory}
                  onLoadPatientAppointments={handleLoadPatientAppointments}
                  onLoadPatientDetails={handleLoadPatientDetails}
                  showOpenFile={showOpenFile}
                />
              );
            })}
          </div>

          <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
            {activePatient ? (
              <div className="wr__drag-overlay">
                <WaitingPatientCard
                  patient={activePatient}
                  onRemove={() => {}}
                  onAdvance={() => {}}
                  inConsultation={activePatient.inConsultation}
                  showOpenFile={showOpenFile}
                  onOpenPaymentHistory={onOpenPaymentHistory}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {showModal && (
        <BookAppointmentModal
          medecinId={medecinId}
          allPatients={allPatients}
          onClose={() => setShowModal(false)}
          onBooked={handleBooked}
          localPatients={patients.filter(
            (patient) => String(patient.source || "").toLowerCase() === "local"
          )}
        />
      )}

      {reschedulePatient && (
        <BookDashboard
          medecinId={medecinId}
          initialPatient={reschedulePatient}
          allPatients={allPatients}
          onClose={() => setReschedulePatient(null)}
          onBooked={handleBooked}
          localPatients={patients.filter(
            (patient) => String(patient.source || "").toLowerCase() === "local"
          )}
        />
      )}

      {paymentModal && (
        <PaymentRequiredModal
          patient={paymentModal.patient}
          onClose={handlePaymentClose}
          onConfirm={handlePaymentConfirm}
          onReschedule={handleReschedule}
          mode={paymentModal.mode || "payment"}
          defaultConsultationPrice={paymentModal.consultationPrice ?? paymentModal.waitingRoomPayment?.consultationPrice ?? paymentModal.consultation?.consultationPrice ?? paymentModal.patient?.consultationPrice ?? 70}
          defaultMedicalActs={paymentModal.medicalActsTotal ?? paymentModal.waitingRoomPayment?.medicalActsTotal ?? paymentModal.consultation?.medicalActs?.reduce((sum, act) => sum + Number(act.price || 0), 0) ?? paymentModal.patient?.medicalActsTotal ?? 0}
          alreadyPaid={paymentModal.alreadyPaid ?? 0}
          initialPaymentHistory={paymentModal.paymentHistory ?? []}
        />
      )}
    </LayoutComponent>
  );
}
